import { useCallback, useEffect, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Loader2, Sparkles } from 'lucide-react';
import api from '@/lib/api';
import Button from '@/components/ui/Button';
import { useAiForgePopup } from '@/hooks/useAiForgePopup';
import ApplyKitDialog from '@/components/admin/branding/ApplyKitDialog';
import KitCard from '@/components/admin/branding/KitCard';
import type { BrandKit, BrandKitDetail, BrandKitSummary } from '@/components/admin/branding/types';

/**
 * Branding Studio — the tenant's brand-kit library. Creation and refinement
 * happen in the AI-Forge studio popup (tool=branding, one popup per kit);
 * this page owns the lifecycle around it: launch, package progress, download,
 * apply-to-storefront with revert, archive. Gated by the `branding_studio`
 * feature flag (the API 404s when off, and the nav item is hidden).
 */
export default function BrandingStudio() {
  const queryClient = useQueryClient();
  const [studioKitId, setStudioKitId] = useState<string | null>(null);
  const [applyTarget, setApplyTarget] = useState<BrandKit | null>(null);
  const [actionError, setActionError] = useState<string | null>(null);

  const refresh = useCallback(() => {
    queryClient.invalidateQueries({ queryKey: ['branding'] });
  }, [queryClient]);

  const { data, isLoading, error } = useQuery<{ kits: BrandKitSummary[] }>({
    queryKey: ['branding', 'kits'],
    queryFn: () => api.get<{ kits: BrandKitSummary[] }>('/admin/branding/kits'),
    refetchInterval: (query) =>
      query.state.data?.kits.some((k) => k.status === 'packaging') ? 2500 : false,
  });

  const { openForge } = useAiForgePopup({
    context: 'branding-studio',
    allowedTypes: ['image'],
    tool: 'branding',
    kit: studioKitId ?? undefined,
    onAssetReceived: () => {},
    onBrandingKitComplete: refresh,
    onClose: refresh,
  });

  // openForge reads `kit` from hook state, so launching for a specific kit is
  // a two-step: set the id, then open on the re-rendered hook.
  const [launchPending, setLaunchPending] = useState(false);
  useEffect(() => {
    if (launchPending && studioKitId) {
      setLaunchPending(false);
      openForge();
    }
  }, [launchPending, studioKitId, openForge]);

  const launchStudio = useCallback((kitId: string) => {
    setStudioKitId(kitId);
    setLaunchPending(true);
  }, []);

  const createKit = useMutation({
    mutationFn: () => api.post<{ kit: BrandKit }>('/admin/branding/kits', {}),
    onSuccess: ({ kit }) => {
      refresh();
      launchStudio(kit.id);
    },
    onError: (e) => setActionError(e instanceof Error ? e.message : 'Failed to create a brand kit'),
  });

  const kitAction = useMutation({
    mutationFn: async (input: { kitId: string; action: 'apply' | 'revert' | 'retry' | 'archive' }) => {
      if (input.action === 'archive') {
        return api.delete(`/admin/branding/kits/${input.kitId}`);
      }
      return api.post(`/admin/branding/kits/${input.kitId}/${input.action}`, {});
    },
    onSuccess: () => {
      setApplyTarget(null);
      refresh();
    },
    onError: (e) => setActionError(e instanceof Error ? e.message : 'Action failed'),
  });

  const download = useCallback(async (kitId: string) => {
    setActionError(null);
    try {
      const { url } = await api.get<{ url: string }>(`/admin/branding/kits/${kitId}/download`);
      window.open(url, '_blank', 'noopener');
    } catch (e) {
      setActionError(e instanceof Error ? e.message : 'Download failed');
    }
  }, []);

  const openApplyDialog = useCallback(async (kitId: string) => {
    setActionError(null);
    try {
      const detail = await api.get<BrandKitDetail>(`/admin/branding/kits/${kitId}`);
      setApplyTarget(detail.kit);
    } catch (e) {
      setActionError(e instanceof Error ? e.message : 'Could not load the kit');
    }
  }, []);

  const kits = data?.kits ?? [];

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-center gap-3">
        <div>
          <h1 className="text-2xl font-semibold text-ink">Branding Studio</h1>
          <p className="mt-1 text-sm text-ink-soft">
            Generate a complete brand identity — logos, colors, fonts, and a full package of
            marketing files — then apply it to your storefront in one click.
          </p>
        </div>
        <div className="ml-auto">
          <Button onClick={() => createKit.mutate()} isLoading={createKit.isPending}>
            <Sparkles className="mr-1.5 h-4 w-4" aria-hidden="true" />
            New brand kit
          </Button>
        </div>
      </div>

      {actionError && <p className="text-sm text-red-600">{actionError}</p>}

      {isLoading ? (
        <div className="flex items-center gap-2 text-ink-muted">
          <Loader2 className="h-4 w-4 animate-spin" aria-hidden="true" /> Loading brand kits…
        </div>
      ) : error ? (
        <p className="text-sm text-red-600">Could not load brand kits.</p>
      ) : kits.length === 0 ? (
        <div className="rounded-xl border border-dashed border-line p-10 text-center">
          <p className="text-ink">No brand kits yet.</p>
          <p className="mt-1 text-sm text-ink-soft">
            Start one and the studio will walk you from a short business brief to logo concepts,
            colors and fonts — then generate business cards, social covers, brand guidelines and
            more.
          </p>
        </div>
      ) : (
        <div className="space-y-3">
          {kits.map((kit) => (
            <KitCard
              key={kit.id}
              kit={kit}
              busy={kitAction.isPending}
              onResumeStudio={launchStudio}
              onDownload={download}
              onApply={openApplyDialog}
              onRevert={(kitId) => kitAction.mutate({ kitId, action: 'revert' })}
              onRetry={(kitId) => kitAction.mutate({ kitId, action: 'retry' })}
              onArchive={(kitId) => kitAction.mutate({ kitId, action: 'archive' })}
            />
          ))}
        </div>
      )}

      {applyTarget && (
        <ApplyKitDialog
          kit={applyTarget}
          busy={kitAction.isPending}
          onConfirm={() => kitAction.mutate({ kitId: applyTarget.id, action: 'apply' })}
          onClose={() => setApplyTarget(null)}
        />
      )}
    </div>
  );
}
